// screen: Registered viewers — segments and consent-only export (change viewer-crm)
// Registered viewers — segments and export (change viewer-crm, design §4.3). Rule builder (AND of groups, OR
// inside a group) with a live count ("≈ N viewers, of whom M agreed to marketing"), saved segments, CSV export of the
// consenting viewers only (allowed fields, never viewing history; 24 h, downloadable once by the requester).
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Plus, Trash2 } from 'lucide-react';
import type { ExportField, SegmentRule, SegmentRuleKind, SegmentRules, ViewerSegment } from '@viewstream/api-client';
import { Badge, Button, Checkbox, Dialog, DialogContent, Input, Select, Skeleton, useToast } from '@viewstream/ui';
import { fmtDateTime, fmtNumber } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/ingest';
import { downloadExport, useDeleteSegment, useExportSegment, useExports, useRecountSegment, useSaveSegment, useSegmentCount, useSegments } from '~/app/viewers';
import { usePlatformLabel, ViewersHeader, VIEWERS_EXPORT, VIEWERS_MANAGE } from '~/app/ViewersNav';

export const Route = createFileRoute('/_layout/t/$slug/viewers/segments')({ component: Screen });

const KINDS: SegmentRuleKind[] = ['active', 'inactive', 'watched', 'platform', 'method', 'joined', 'consent', 'status'];
const FIELDS: ExportField[] = ['email', 'name', 'lang', 'signed_up', 'last_seen', 'platforms'];

function defaults(kind: SegmentRuleKind): SegmentRule {
  switch (kind) {
    case 'active': case 'inactive': return { kind, days: 30 };
    case 'watched': return { kind, target: 'channel', id: 'main', min_minutes: 30, days: 30 };
    case 'platform': return { kind, platform: 'tizen' };
    case 'method': return { kind, method: 'password' };
    case 'joined': return { kind, from: new Date(Date.now() - 30 * 86_400_000).toISOString() };
    case 'consent': return { kind, purpose: 'marketing', granted: true };
    case 'status': return { kind, status: 'active' };
    default: return { kind };
  }
}

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = React.useState(v);
  React.useEffect(() => { const h = setTimeout(() => setD(v), ms); return () => clearTimeout(h); }, [v, ms]);
  return d;
}

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale, tz } = useLocale();
  const toast = useToast();
  React.useEffect(() => { document.title = appTitle(t`Segments`); }, [t]);
  const segs = useSegments(tenant.slug);
  const canManage = can(scopes, VIEWERS_MANAGE);
  const canExport = can(scopes, VIEWERS_EXPORT);
  const exports = useExports(tenant.slug, canExport);
  const save = useSaveSegment(tenant.slug);
  const del = useDeleteSegment(tenant.slug);
  const recount = useRecountSegment(tenant.slug);
  const exp = useExportSegment(tenant.slug);
  const [edit, setEdit] = React.useState<{ id?: string; name: string; rules: SegmentRules } | null>(null);
  const [exportOf, setExportOf] = React.useState<ViewerSegment | null>(null);
  const [fields, setFields] = React.useState<ExportField[]>(['email', 'name']);
  const fail = (e: unknown) => toast.push({ title: t`Something went wrong`, description: problemText(e), tone: 'danger' });
  const n = (x: number | null | undefined) => (x === null || x === undefined ? '—' : fmtNumber(x, locale, { maximumFractionDigits: 0 }));
  const fieldLabel: Record<ExportField, string> = { email: t`E-mail`, name: t`Name`, lang: t`Language`, signed_up: t`Joined`, last_seen: t`Last seen`, platforms: t`Platforms` };
  const statusLabel: Record<string, string> = { queued: t`Queued`, running: t`Preparing`, ready: t`Ready`, downloaded: t`Downloaded`, expired: t`Expired`, failed: t`Failed` };

  return (
    <div>
      <ViewersHeader tab="segments">
        {canManage ? <Button onClick={() => setEdit({ name: '', rules: { groups: [[defaults('active')]] } })} data-testid="segment-new"><Plus className="size-4" aria-hidden /> {t`New segment`}</Button> : null}
      </ViewersHeader>
      {segs.isPending ? <Skeleton className="h-24 w-full" /> : (segs.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">{t`No segments yet. A segment is a saved audience, for example viewers active in the last 30 days who agreed to marketing e-mails.`}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border" data-testid="segments">
          {segs.data!.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm">
              <div>
                <div className="font-medium">{s.name}</div>
                <div className="text-fg-muted">
                  {s.counted_at ? t`≈ ${n(s.last_count)} viewers, of whom ${n(s.last_count_consenting)} agreed to marketing` : t`Not counted yet`}
                  {s.last_count_no_analytics ? <> · {t`${n(s.last_count_no_analytics)} left out without consent to usage analytics`}</> : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="ghost" onClick={() => recount.mutate(s.id, { onError: fail })}>{t`Count now`}</Button>
                {canManage ? <Button size="sm" variant="ghost" onClick={() => setEdit({ id: s.id, name: s.name, rules: s.rules })}>{t`Edit`}</Button> : null}
                {canExport ? <Button size="sm" variant="secondary" onClick={() => setExportOf(s)}>{t`Export CSV…`}</Button> : null}
                {canManage ? <Button size="sm" variant="ghost" aria-label={t`Delete`} onClick={() => del.mutate(s.id, { onError: fail })}><Trash2 className="size-4" aria-hidden /></Button> : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canExport ? (
        <section className="mt-6">
          <h2 className="mb-2 text-lg font-semibold">{t`Exports`}</h2>
          <p className="mb-2 max-w-3xl text-sm text-fg-muted">{t`Each file holds only viewers who agreed to marketing e-mails, never their viewing history. It is kept for 24 hours and can be downloaded once, by whoever asked for it. Every export is recorded.`}</p>
          {(exports.data ?? []).length === 0 ? <p className="text-sm text-fg-muted">{t`No exports in the last 7 days.`}</p> : (
            <ul className="divide-y divide-border rounded-lg border border-border text-sm" data-testid="viewer-exports">
              {exports.data!.map((x) => (
                <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <span>{x.kind === 'segment_csv' ? t`Segment CSV` : t`Viewer data package`} · {fmtDateTime(x.created_at, { locale, tz })}{x.rows !== null ? ` · ${n(x.rows)} ${t`rows`}` : ''}</span>
                  <span className="flex items-center gap-2">
                    <Badge tone={x.status === 'ready' ? 'ok' : x.status === 'failed' ? 'danger' : 'neutral'}>{statusLabel[x.status] ?? x.status}</Badge>
                    {x.status === 'ready' ? <Button size="sm" onClick={() => downloadExport(x).catch(fail)}>{t`Download`}</Button> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      <Dialog open={edit !== null} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        {edit ? (
          <DialogContent title={edit.id ? t`Edit segment` : t`New segment`} closeLabel={t`Close`} className="max-w-3xl">
            <SegmentEditor value={edit} onChange={setEdit} />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEdit(null)}>{t`Cancel`}</Button>
              <Button disabled={!edit.name.trim() || save.isPending} onClick={() => save.mutate(edit, { onSuccess: () => setEdit(null), onError: fail })} data-testid="segment-save">{t`Save`}</Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog open={exportOf !== null} onOpenChange={(o) => { if (!o) setExportOf(null); }}>
        {exportOf ? (
          <DialogContent title={t`Export “${exportOf.name}”`} closeLabel={t`Close`}
            description={t`Only viewers who agreed to marketing e-mails are included. Choose the fields:`}>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {FIELDS.map((f) => (
                <label key={f} className="flex items-center gap-2">
                  <Checkbox checked={fields.includes(f)} onCheckedChange={(c) => setFields((xs) => (c ? [...xs, f] : xs.filter((y) => y !== f)))} />
                  {fieldLabel[f]}
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setExportOf(null)}>{t`Cancel`}</Button>
              <Button disabled={fields.length === 0 || exp.isPending} onClick={() => exp.mutate({ id: exportOf.id, fields }, {
                onSuccess: () => { setExportOf(null); toast.push({ title: t`The file is being prepared`, description: t`It appears under Exports in a moment.` }); },
                onError: fail,
              })} data-testid="segment-export">{t`Prepare the file`}</Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}

function SegmentEditor({ value, onChange }: { value: { id?: string; name: string; rules: SegmentRules }; onChange: (v: { id?: string; name: string; rules: SegmentRules }) => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const debounced = useDebounced(value.rules, 400);
  const count = useSegmentCount(tenant.slug, debounced);
  const set = (rules: SegmentRules) => onChange({ ...value, rules });
  const groups = value.rules.groups;
  const n = (x: number) => fmtNumber(x, locale, { maximumFractionDigits: 0 });
  return (
    <div className="space-y-3">
      <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder={t`Segment name`} aria-label={t`Segment name`} data-testid="segment-name" />
      {groups.map((g, gi) => (
        <div key={gi} className="rounded-md border border-border p-2">
          {gi > 0 ? <div className="mb-1 text-xs font-semibold text-fg-muted">{t`and`}</div> : null}
          {g.map((r, ri) => (
            <div key={ri} className="mb-1 flex flex-wrap items-center gap-2">
              {ri > 0 ? <span className="text-xs text-fg-muted">{t`or`}</span> : null}
              <RuleRow rule={r} onChange={(nr) => set({ groups: groups.map((x, i) => (i === gi ? x.map((y, j) => (j === ri ? nr : y)) : x)) })}
                onRemove={() => set({ groups: groups.map((x, i) => (i === gi ? x.filter((_, j) => j !== ri) : x)).filter((x) => x.length > 0) })} />
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => set({ groups: groups.map((x, i) => (i === gi ? [...x, defaults('platform')] : x)) })}>{t`+ or`}</Button>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={() => set({ groups: [...groups, [defaults('active')]] })}>{t`+ and`}</Button>
      <p className="text-sm" data-testid="segment-count">
        {count.data ? t`≈ ${n(count.data.total)} viewers, of whom ${n(count.data.consenting)} agreed to marketing` : count.isError ? problemText(count.error) : '…'}
        {count.data?.watched && count.data.no_analytics > 0 ? <span className="text-fg-muted"> · {t`${n(count.data.no_analytics)} left out without consent to usage analytics`}</span> : null}
      </p>
    </div>
  );
}

function RuleRow({ rule, onChange, onRemove }: { rule: SegmentRule; onChange: (r: SegmentRule) => void; onRemove: () => void }) {
  const { t } = useLingui();
  const platform = usePlatformLabel();
  const kindLabel: Record<SegmentRuleKind, string> = {
    active: t`Active in the last`, inactive: t`Not seen for`, watched: t`Watched`, platform: t`Platform`, site: t`Sign-up site`, method: t`Sign-in method`,
    joined: t`Joined from`, consent: t`Consent`, status: t`Status`,
  };
  const num = (k: 'days' | 'min_minutes', label: string) => (
    <span className="inline-flex items-center gap-1">
      <Input type="number" min={1} className="w-20" value={rule[k] ?? ''} onChange={(e) => onChange({ ...rule, [k]: Number(e.target.value) || undefined })} aria-label={label} />
      {label}
    </span>
  );
  return (
    <>
      <Select ariaLabel={t`Condition`} value={rule.kind} onValueChange={(k) => onChange(defaults(k as SegmentRuleKind))}
        options={KINDS.map((k) => ({ value: k, label: kindLabel[k] }))} />
      {rule.kind === 'active' || rule.kind === 'inactive' ? num('days', t`days`) : null}
      {rule.kind === 'watched' ? (
        <>
          <Select ariaLabel={t`What`} value={rule.target ?? 'channel'} onValueChange={(v) => onChange({ ...rule, target: v as SegmentRule['target'], id: v === 'channel' ? 'main' : '' })}
            options={[{ value: 'channel', label: t`a channel (slug)` }, { value: 'series', label: t`a show (id)` }, { value: 'asset', label: t`a video (id)` }]} />
          <Input className="w-56" dir="ltr" value={rule.id ?? ''} onChange={(e) => onChange({ ...rule, id: e.target.value.trim() })} aria-label={t`Channel slug or id`} />
          {num('min_minutes', t`minutes at least, in`)}{num('days', t`days`)}
        </>
      ) : null}
      {rule.kind === 'platform' ? (
        <Select ariaLabel={t`Platform`} value={rule.platform ?? 'tizen'} onValueChange={(v) => onChange({ ...rule, platform: v as SegmentRule['platform'] })}
          options={['tizen', 'webos', 'androidtv', 'firetv', 'vidaa', 'web', 'ios', 'android'].map((p) => ({ value: p, label: platform(p) }))} />
      ) : null}
      {rule.kind === 'method' ? (
        <Select ariaLabel={t`Sign-in method`} value={rule.method ?? 'password'} onValueChange={(v) => onChange({ ...rule, method: v as SegmentRule['method'] })}
          options={[{ value: 'password', label: t`Password` }, { value: 'google', label: 'Google' }, { value: 'apple', label: 'Apple' }]} />
      ) : null}
      {rule.kind === 'joined' ? (
        <Input type="date" className="w-44" value={(rule.from ?? '').slice(0, 10)} onChange={(e) => onChange({ ...rule, from: e.target.value ? new Date(e.target.value).toISOString() : undefined })} aria-label={t`Joined from`} />
      ) : null}
      {rule.kind === 'consent' ? (
        <Select ariaLabel={t`Consent`} value={`${rule.purpose}:${rule.granted}`} onValueChange={(v) => { const [p, g] = v.split(':'); onChange({ ...rule, purpose: p as SegmentRule['purpose'], granted: g === 'true' }); }}
          options={[{ value: 'marketing:true', label: t`agreed to marketing` }, { value: 'marketing:false', label: t`no marketing` }, { value: 'analytics:true', label: t`agreed to usage analytics` }, { value: 'personalisation:true', label: t`agreed to personalisation` }]} />
      ) : null}
      {rule.kind === 'status' ? (
        <Select ariaLabel={t`Status`} value={rule.status ?? 'active'} onValueChange={(v) => onChange({ ...rule, status: v as SegmentRule['status'] })}
          options={[{ value: 'active', label: t`Active` }, { value: 'blocked', label: t`Blocked` }]} />
      ) : null}
      <Button size="sm" variant="ghost" aria-label={t`Remove condition`} onClick={onRemove}><Trash2 className="size-4" aria-hidden /></Button>
    </>
  );
}
